/**
 * Device pairing approval flow.
 *
 * When a new device connects for the first time, the gateway returns
 * NOT_PAIRED. The device must be approved by an existing operator
 * (via CLI: `openclaw devices approve`).
 *
 * This module manages the retry loop:
 * 1. Show "Waiting for Approval" UI
 * 2. Retry connection every 10 seconds
 * 3. On success → transition to connected state
 * 4. On expiry (5 min) → stop retrying, show "Retry" button
 */

import { PAIRING_EXPIRY_MS, PAIRING_RETRY_INTERVAL_MS } from '../utils/constants';

export type PairingStatus =
  | 'idle'       // Not in a pairing flow
  | 'pending'    // Waiting for approval
  | 'expired'    // Approval window expired
  | 'approved'   // Device was approved
  | 'cancelled'; // User cancelled

export interface PairingState {
  status: PairingStatus;
  deviceId: string;
  gatewayId: string;
  startedAt: number;
  /** Remaining time in milliseconds */
  remainingMs: number;
}

export type PairingRetryFn = () => Promise<boolean>;

/**
 * Manages the pairing approval retry loop.
 *
 * Usage:
 * ```ts
 * const loop = new PairingLoop(deviceId, gatewayId, async () => {
 *   // Attempt to reconnect — returns true if approved
 *   return await attemptReconnect();
 * });
 *
 * loop.onStateChange = (state) => updateUI(state);
 * loop.start();
 *
 * // Later:
 * loop.cancel();
 * ```
 */
export class PairingLoop {
  readonly deviceId: string;
  readonly gatewayId: string;
  private retryFn: PairingRetryFn;
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;
  private _status: PairingStatus = 'idle';

  /** Called when pairing state changes */
  onStateChange: ((state: PairingState) => void) | null = null;

  constructor(deviceId: string, gatewayId: string, retryFn: PairingRetryFn) {
    this.deviceId = deviceId;
    this.gatewayId = gatewayId;
    this.retryFn = retryFn;
  }

  /** Start the pairing retry loop */
  start(): void {
    this.stop();
    this.startedAt = Date.now();
    this._status = 'pending';
    this.emitState();

    // Retry connection every PAIRING_RETRY_INTERVAL_MS
    this.retryTimer = setInterval(async () => {
      const elapsed = Date.now() - this.startedAt;
      if (elapsed >= PAIRING_EXPIRY_MS) {
        this._status = 'expired';
        this.stop();
        this.emitState();
        return;
      }

      try {
        const approved = await this.retryFn();
        if (approved) {
          this._status = 'approved';
          this.stop();
          this.emitState();
        }
      } catch {
        // Retry will continue
      }
    }, PAIRING_RETRY_INTERVAL_MS);

    // Update countdown every second
    this.countdownTimer = setInterval(() => {
      if (this._status !== 'pending') return;
      this.emitState();
    }, 1000);
  }

  /** Cancel the pairing flow */
  cancel(): void {
    this._status = 'cancelled';
    this.stop();
    this.emitState();
  }

  /** Restart the pairing flow (after expiry) */
  restart(): void {
    this.start();
  }

  /** Stop all timers */
  private stop(): void {
    if (this.retryTimer !== null) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  get status(): PairingStatus {
    return this._status;
  }

  get remainingMs(): number {
    if (this._status !== 'pending') return 0;
    return Math.max(0, PAIRING_EXPIRY_MS - (Date.now() - this.startedAt));
  }

  private emitState(): void {
    this.onStateChange?.({
      status: this._status,
      deviceId: this.deviceId,
      gatewayId: this.gatewayId,
      startedAt: this.startedAt,
      remainingMs: this.remainingMs,
    });
  }
}
